import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { consolidateBreakdown } from './invoiceCalc';
import { wrapTracked } from './pdfText';

// Static layout colors (not brand-dependent)
const GREY = rgb(0.45, 0.45, 0.45);
const LIGHT_LINE = rgb(0.85, 0.85, 0.85);
const ZEBRA = rgb(0.98, 0.98, 0.98);

// Fallback brand colors (used if brandAssets.colors missing)
const FALLBACK_PRIMARY = { r: 0.4, g: 0, b: 0.6 };       // #660099
const FALLBACK_ACCENT = { r: 0.72, g: 0.53, b: 0.04 };    // #B8860B
const FALLBACK_TEXT = { r: 0.1, g: 0.1, b: 0.1 };          // #1A1A1A

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const TOP_MARGIN = 50;
const BOTTOM_MARGIN = 40;
const LEFT_MARGIN = 50;
const RIGHT_MARGIN = PAGE_WIDTH - 50;
const TABLE_WIDTH = RIGHT_MARGIN - LEFT_MARGIN;
const ROW_HEIGHT = 24;
const FOOTER_BLOCK_HEIGHT = 140;
const WRAP_LINE_HEIGHT = 11;
const DESC_WIDTH = TABLE_WIDTH - 120;

export const DEFAULT_TERMS = 'Payment due upon receipt. Coverage is bound upon receipt of payment in cleared funds.';

/** The line description the PDF prints when the builder supplies none. */
export function defaultLineDescription(li) {
  return `${li.lob} — Policy #${li.policyNumber || 'N/A'} (${li.term || 'N/A'})`;
}

function lineDescription(li) {
  const d = (li.description || '').trim();
  return d || defaultLineDescription(li);
}

/**
 * Greedy word wrap for line descriptions. A spaced dash rides at the end of
 * the line before it, so no wrapped line opens on "–" or "—".
 */
function wrapDescription(font, text, size, maxWidth) {
  const words = [];
  for (const w of String(text).split(/\s+/).filter(Boolean)) {
    if ((w === '–' || w === '—') && words.length) words[words.length - 1] += ` ${w}`;
    else words.push(w);
  }
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (cur && font.widthOfTextAtSize(test, size) > maxWidth) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

/**
 * Generate a FIG invoice PDF with multi-page support, zebra striping, and brand assets.
 *
 * @param {Object} invoiceData
 * @param {Object} insured
 * @param {Object} settings
 * @param {string} invoiceNumber
 * @param {Object} [brandAssets] — { logoBytes, leafBytes, colors }
 */
export async function generateInvoicePDF(invoiceData, insured, settings, invoiceNumber, brandAssets) {
  const pdfDoc = await PDFDocument.create();

  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
  const timesBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);

  // Brand colors
  const bc = brandAssets?.colors || {};
  const PRIMARY = toRgb(bc.primary, FALLBACK_PRIMARY);
  const ACCENT = toRgb(bc.accent, FALLBACK_ACCENT);
  const DARK = toRgb(bc.text, FALLBACK_TEXT);

  // Embed logo if available
  let logoImage = null;
  if (brandAssets?.logoBytes) {
    try {
      logoImage = await pdfDoc.embedPng(brandAssets.logoBytes);
    } catch {
      try { logoImage = await pdfDoc.embedJpg(brandAssets.logoBytes); } catch { /* fallback to text */ }
    }
  }

  // Embed leaf if available
  let leafImage = null;
  if (brandAssets?.leafBytes) {
    try {
      leafImage = await pdfDoc.embedPng(brandAssets.leafBytes);
    } catch {
      try { leafImage = await pdfDoc.embedJpg(brandAssets.leafBytes); } catch { /* skip */ }
    }
  }

  const s = (v) => v || '';
  const pages = [];
  let currentPage;
  let y;

  // ---- Helpers ----

  // Watermark leaf — rendered first on every page, behind all content
  function drawWatermarkLeaf(page) {
    if (!leafImage) return;
    const leafDims = leafImage.scale(1);
    const wmW = 600;
    const wmH = (leafDims.height / leafDims.width) * wmW;
    // Anchored so ~60% visible, right portion bleeds off page edge
    const wmX = PAGE_WIDTH - 400;
    const wmY = (PAGE_HEIGHT - wmH) / 2;
    page.drawImage(leafImage, { x: wmX, y: wmY, width: wmW, height: wmH, opacity: 0.06 });
  }

  function drawTableHeader(page, atY) {
    page.drawLine({ start: { x: LEFT_MARGIN, y: atY }, end: { x: RIGHT_MARGIN, y: atY }, thickness: 1, color: DARK });
    atY -= 14;
    page.drawText('Description', { x: LEFT_MARGIN + 4, y: atY, font: helveticaBold, size: 9, color: DARK });
    const amtHeaderW = helveticaBold.widthOfTextAtSize('Amount', 9);
    page.drawText('Amount', { x: RIGHT_MARGIN - amtHeaderW - 4, y: atY, font: helveticaBold, size: 9, color: DARK });
    atY -= 6;
    page.drawLine({ start: { x: LEFT_MARGIN, y: atY }, end: { x: RIGHT_MARGIN, y: atY }, thickness: 0.5, color: DARK });
    return atY;
  }

  const dateLine = new Date(invoiceData.generatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  function drawContinuationHeader(page) {
    let py = PAGE_HEIGHT - TOP_MARGIN;

    // Left: compact text header (logo only on page 1)
    page.drawText('F.I.G.', { x: LEFT_MARGIN, y: py, font: timesBold, size: 14, color: DARK });
    page.drawText('FRAGOS INSURANCE GROUP, LLC', {
      x: LEFT_MARGIN, y: py - 14, font: helveticaBold, size: 8, color: GREY,
    });

    // Right: INVOICE + number + date
    const invLabelW = helveticaBold.widthOfTextAtSize('INVOICE', 14);
    page.drawText('INVOICE', { x: RIGHT_MARGIN - invLabelW, y: py, font: helveticaBold, size: 14, color: DARK });

    const numText = `#${invoiceNumber}`;
    const numW = helvetica.widthOfTextAtSize(numText, 9);
    page.drawText(numText, { x: RIGHT_MARGIN - numW, y: py - 14, font: helvetica, size: 9, color: GREY });

    const dateW = helvetica.widthOfTextAtSize(dateLine, 9);
    page.drawText(dateLine, { x: RIGHT_MARGIN - dateW, y: py - 26, font: helvetica, size: 9, color: GREY });

    py -= 38;
    page.drawLine({ start: { x: LEFT_MARGIN, y: py }, end: { x: RIGHT_MARGIN, y: py }, thickness: 1, color: ACCENT });
    return py - 10;
  }

  function addContinuationPage(withTableHeader) {
    const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    pages.push(page);
    drawWatermarkLeaf(page);

    let py = drawContinuationHeader(page);
    if (withTableHeader) py = drawTableHeader(page, py);

    currentPage = page;
    y = py;
  }

  function ensureSpace(needed) {
    if (y - needed < BOTTOM_MARGIN) {
      addContinuationPage(true);
    }
  }

  // ========== PAGE 1 — FULL HEADER ==========
  currentPage = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  pages.push(currentPage);
  drawWatermarkLeaf(currentPage);
  y = PAGE_HEIGHT - TOP_MARGIN;

  // Logo or text fallback
  if (logoImage) {
    const logoDims = logoImage.scale(1);
    const logoH = 60;
    const logoW = (logoDims.width / logoDims.height) * logoH;
    currentPage.drawImage(logoImage, { x: LEFT_MARGIN, y: y - logoH + 16, width: logoW, height: logoH });
  } else {
    currentPage.drawText('F.I.G.', { x: LEFT_MARGIN, y, font: timesBold, size: 28, color: DARK });
  }

  // Right: INVOICE title + number + date
  const titleWidth = helveticaBold.widthOfTextAtSize('INVOICE', 24);
  currentPage.drawText('INVOICE', { x: RIGHT_MARGIN - titleWidth, y, font: helveticaBold, size: 24, color: DARK });

  const numLine = `#${invoiceNumber}`;
  const numWidth = helvetica.widthOfTextAtSize(numLine, 10);
  currentPage.drawText(numLine, { x: RIGHT_MARGIN - numWidth, y: y - 16, font: helvetica, size: 10, color: GREY });

  const dateWidth = helvetica.widthOfTextAtSize(dateLine, 10);
  currentPage.drawText(dateLine, { x: RIGHT_MARGIN - dateWidth, y: y - 30, font: helvetica, size: 10, color: GREY });

  y -= 50;
  currentPage.drawLine({ start: { x: LEFT_MARGIN, y }, end: { x: RIGHT_MARGIN, y }, thickness: 1.5, color: ACCENT });

  // FROM
  y -= 20;
  currentPage.drawText('FROM:', { x: LEFT_MARGIN, y, font: helveticaBold, size: 8, color: GREY });
  y -= 14;
  const fromLines = [
    s(settings.AgencyLegalName),
    s(settings.AgencyAddress),
    [s(settings.AgencyCity), s(settings.AgencyState), s(settings.AgencyZip)].filter(Boolean).join(', '),
  ].filter(Boolean);
  for (const line of fromLines) {
    currentPage.drawText(line, { x: LEFT_MARGIN, y, font: helvetica, size: 9, color: DARK });
    y -= 13;
  }

  // BILL TO
  const billToX = PAGE_WIDTH / 2 + 20;
  let billY = y + fromLines.length * 13 + 14;
  currentPage.drawText('BILL TO:', { x: billToX, y: billY, font: helveticaBold, size: 8, color: GREY });
  billY -= 14;
  const billLines = [
    s(insured.CompanyName),
    s(insured.MailingAddress),
    [s(insured.MailingCity), s(insured.MailingState), s(insured.MailingZip)].filter(Boolean).join(', '),
  ].filter(Boolean);
  for (const line of billLines) {
    currentPage.drawText(line, { x: billToX, y: billY, font: helvetica, size: 9, color: DARK });
    billY -= 13;
  }

  y = Math.min(y, billY) - 10;

  // REFERENCE + JOB
  const hasRef = !!invoiceData.referenceNumber;
  const hasJob = !!invoiceData.job;
  if (hasRef || hasJob) {
    currentPage.drawLine({ start: { x: LEFT_MARGIN, y }, end: { x: RIGHT_MARGIN, y }, thickness: 0.5, color: LIGHT_LINE });
    y -= 16;
    if (hasRef) {
      currentPage.drawText('Ref: ', { x: LEFT_MARGIN, y, font: helveticaBold, size: 9, color: GREY });
      const refLabelW = helveticaBold.widthOfTextAtSize('Ref: ', 9);
      currentPage.drawText(invoiceData.referenceNumber, { x: LEFT_MARGIN + refLabelW, y, font: helvetica, size: 9, color: DARK });
      y -= 14;
    }
    if (hasJob) {
      currentPage.drawText('Job: ', { x: LEFT_MARGIN, y, font: helveticaBold, size: 9, color: GREY });
      const jobLabelW = helveticaBold.widthOfTextAtSize('Job: ', 9);
      currentPage.drawText(invoiceData.job, { x: LEFT_MARGIN + jobLabelW, y, font: helvetica, size: 9, color: DARK });
      y -= 14;
    }
    y -= 4;
  }

  // TABLE HEADER
  y -= 10;
  y = drawTableHeader(currentPage, y);

  // ========== LINE ITEMS ==========
  const SUB_ROW_HEIGHT = 14;
  const displayMode = invoiceData.displayMode;
  const isBreakdownMode = displayMode === 'itemized' || displayMode === 'consolidated';

  if (!isBreakdownMode) {
    // ---- BUNDLED MODE ----
    for (let i = 0; i < invoiceData.lineItems.length; i++) {
      const li = invoiceData.lineItems[i];
      // Row grows one wrap line at a time; a one-line description keeps ROW_HEIGHT.
      const descLines = wrapDescription(helvetica, lineDescription(li), 9, DESC_WIDTH);
      const rowHeight = ROW_HEIGHT + (descLines.length - 1) * WRAP_LINE_HEIGHT;
      ensureSpace(rowHeight);

      if (i % 2 === 0) {
        currentPage.drawRectangle({
          x: LEFT_MARGIN, y: y - rowHeight, width: TABLE_WIDTH, height: rowHeight,
          color: ZEBRA,
        });
      }

      y -= 18;
      const firstLineY = y;
      for (let n = 0; n < descLines.length; n++) {
        if (n > 0) y -= WRAP_LINE_HEIGHT;
        currentPage.drawText(descLines[n], { x: LEFT_MARGIN + 4, y, font: helvetica, size: 9, color: DARK });
      }

      // Amount aligns with the first description line.
      const amtText = formatCurrency(li.amount);
      const amtW = helvetica.widthOfTextAtSize(amtText, 9);
      currentPage.drawText(amtText, { x: RIGHT_MARGIN - amtW - 4, y: firstLineY, font: helvetica, size: 9, color: DARK });

      y -= 6;
      currentPage.drawLine({ start: { x: LEFT_MARGIN, y }, end: { x: RIGHT_MARGIN, y }, thickness: 0.3, color: LIGHT_LINE });
    }
  } else {
    // ---- ITEMIZED / CONSOLIDATED MODE ----
    for (let i = 0; i < invoiceData.lineItems.length; i++) {
      const li = invoiceData.lineItems[i];
      const b = li.breakdown || {};
      const c = displayMode === 'consolidated' ? consolidateBreakdown(b) : null;

      // Build sub-rows (only non-zero values)
      const subRows = (c ? [
        { label: 'Premium', value: c.premium },
        { label: 'State Tax', value: c.stateTax },
        { label: 'Policy Fees', value: c.fees },
      ] : [
        { label: 'Premium', value: b.premium },
        { label: 'State Tax', value: b.stateTax },
        { label: 'Stamping Fee', value: b.stampingFee },
        { label: 'Inspection Fee', value: b.inspectionFee },
        { label: 'Policy Fee', value: b.policyFee },
        { label: 'Agency Fee', value: b.agencyFee },
        { label: 'Broker Fee', value: b.brokerFee },
        ...(b.additionalFees || []).filter(f => f.amount > 0).map(f => ({ label: f.label, value: f.amount })),
      ]).filter(r => r.value > 0);

      const headerLines = wrapDescription(helveticaBold, lineDescription(li), 9, TABLE_WIDTH - 20);

      // Group height: header(20 + wrap lines) + sub-rows(14 each) + subtotal(18) + gap(8)
      const groupHeight = 20 + (headerLines.length - 1) * WRAP_LINE_HEIGHT +
        subRows.length * SUB_ROW_HEIGHT + 18 + 8;
      ensureSpace(groupHeight);

      // Zebra stripe for the whole group (alternating per policy group)
      if (i % 2 === 0) {
        currentPage.drawRectangle({
          x: LEFT_MARGIN, y: y - groupHeight, width: TABLE_WIDTH, height: groupHeight,
          color: ZEBRA,
        });
      }

      // Policy group header (bold)
      y -= 14;
      for (let n = 0; n < headerLines.length; n++) {
        if (n > 0) y -= WRAP_LINE_HEIGHT;
        currentPage.drawText(headerLines[n], { x: LEFT_MARGIN + 4, y, font: helveticaBold, size: 9, color: DARK });
      }
      y -= 6;

      // Sub-rows (indented, grey)
      for (const sr of subRows) {
        y -= SUB_ROW_HEIGHT;
        currentPage.drawText(sr.label, { x: LEFT_MARGIN + 20, y, font: helvetica, size: 8, color: GREY });
        const srAmt = formatCurrency(sr.value);
        const srW = helvetica.widthOfTextAtSize(srAmt, 8);
        currentPage.drawText(srAmt, { x: RIGHT_MARGIN - srW - 4, y, font: helvetica, size: 8, color: GREY });
      }

      // Subtotal row with rule
      y -= 4;
      currentPage.drawLine({ start: { x: LEFT_MARGIN + 16, y }, end: { x: RIGHT_MARGIN, y }, thickness: 0.3, color: LIGHT_LINE });
      y -= 14;
      currentPage.drawText('Subtotal', { x: LEFT_MARGIN + 20, y, font: helveticaBold, size: 8, color: DARK });
      const subAmt = formatCurrency(li.amount);
      const subW = helveticaBold.widthOfTextAtSize(subAmt, 9);
      currentPage.drawText(subAmt, { x: RIGHT_MARGIN - subW - 4, y, font: helveticaBold, size: 9, color: DARK });

      // Gap between groups
      y -= 8;
      if (i < invoiceData.lineItems.length - 1) {
        currentPage.drawLine({ start: { x: LEFT_MARGIN, y }, end: { x: RIGHT_MARGIN, y }, thickness: 0.3, color: LIGHT_LINE });
      }
    }
  }

  // ========== NOTE (flows after the table, breaks across pages by line) ==========
  const note = (invoiceData.note || '').trim();
  if (note) {
    const noteLines = note.split(/\r?\n/)
      .flatMap(para => (para.trim() ? wrapTracked(helvetica, para, 9, TABLE_WIDTH - 8) : ['']));
    if (y - 30 - WRAP_LINE_HEIGHT < BOTTOM_MARGIN) addContinuationPage(false);
    y -= 20;
    currentPage.drawText('NOTE:', { x: LEFT_MARGIN, y, font: helveticaBold, size: 8, color: GREY });
    y -= 14;
    for (const line of noteLines) {
      if (y < BOTTOM_MARGIN) {
        addContinuationPage(false);
        y -= 4;
      }
      if (line) currentPage.drawText(line, { x: LEFT_MARGIN + 4, y, font: helvetica, size: 9, color: DARK });
      y -= 12;
    }
  }

  // ========== FOOTER BLOCK (anchored to page bottom) ==========
  // Terms wrap to the table width; each extra line lifts the whole block.
  const termsText = (invoiceData.terms || '').trim() || DEFAULT_TERMS;
  const termsLines = wrapTracked(helveticaOblique, termsText, 9, TABLE_WIDTH);
  const footerHeight = FOOTER_BLOCK_HEIGHT + (termsLines.length - 1) * WRAP_LINE_HEIGHT;

  if (y - footerHeight < BOTTOM_MARGIN) {
    addContinuationPage(false);
  }

  y = BOTTOM_MARGIN + footerHeight;

  // Grand total
  y -= 14;
  currentPage.drawLine({ start: { x: RIGHT_MARGIN - 180, y: y + 6 }, end: { x: RIGHT_MARGIN, y: y + 6 }, thickness: 1.5, color: ACCENT });
  currentPage.drawText('GRAND TOTAL', { x: RIGHT_MARGIN - 176, y: y - 8, font: helveticaBold, size: 10, color: DARK });
  const totalText = formatCurrency(invoiceData.grandTotal);
  const totalW = helveticaBold.widthOfTextAtSize(totalText, 14);
  currentPage.drawText(totalText, { x: RIGHT_MARGIN - totalW - 4, y: y - 10, font: helveticaBold, size: 14, color: DARK });

  // Payment block
  y -= 36;
  currentPage.drawLine({ start: { x: LEFT_MARGIN, y }, end: { x: RIGHT_MARGIN, y }, thickness: 0.5, color: LIGHT_LINE });
  y -= 14;
  currentPage.drawText(`Please make all checks payable to: ${s(settings.PaymentPayableTo)}`, {
    x: LEFT_MARGIN, y, font: helveticaBold, size: 9, color: DARK,
  });
  y -= 16;
  currentPage.drawText('ACH / Wire Instructions:', { x: LEFT_MARGIN, y, font: helveticaBold, size: 9, color: GREY });
  y -= 13;
  const payLines = [
    `Bank Name: ${s(settings.PaymentBankName)}`,
    `Bank Routing: ${s(settings.PaymentRouting)}`,
    `Account Number: ${s(settings.PaymentAccount)}`,
  ];
  for (const line of payLines) {
    currentPage.drawText(line, { x: LEFT_MARGIN + 10, y, font: helvetica, size: 9, color: DARK });
    y -= 12;
  }

  // Terms + contact
  y -= 10;
  termsLines.forEach((line, i) => {
    if (i > 0) y -= WRAP_LINE_HEIGHT;
    const termsW = helveticaOblique.widthOfTextAtSize(line, 9);
    currentPage.drawText(line, {
      x: (PAGE_WIDTH - termsW) / 2, y, font: helveticaOblique, size: 9, color: GREY,
    });
  });

  y -= 14;
  const addrParts = [s(settings.AgencyAddress), [s(settings.AgencyCity), s(settings.AgencyState), s(settings.AgencyZip)].filter(Boolean).join(', ')].filter(Boolean).join(', ');
  const footerParts = [s(settings.AgencyLegalName), addrParts, s(settings.AgencyPhone), s(settings.AgencyWebsite)].filter(Boolean);
  const footerLine = footerParts.join('  \u2022  ');
  const footerW = helvetica.widthOfTextAtSize(footerLine, 8);
  currentPage.drawText(footerLine, {
    x: (PAGE_WIDTH - footerW) / 2, y, font: helvetica, size: 8, color: GREY,
  });

  // ========== LEAF on last page ==========

  // ========== POST-RENDER: Page N of M on continuation pages ==========
  const totalPages = pages.length;
  if (totalPages > 1) {
    for (let i = 1; i < totalPages; i++) {
      const pg = pages[i];
      const pageLabel = `Page ${i + 1} of ${totalPages}`;
      const labelW = helvetica.widthOfTextAtSize(pageLabel, 9);
      pg.drawText(pageLabel, {
        x: RIGHT_MARGIN - labelW, y: PAGE_HEIGHT - TOP_MARGIN - 38, font: helvetica, size: 9, color: GREY,
      });
    }
  }

  return pdfDoc.save();
}

function toRgb(c, fallback) {
  const v = c || fallback;
  return rgb(v.r, v.g, v.b);
}

function formatCurrency(amount) {
  const num = Number(amount) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
